<template>
  <create-form v-if="isCreateModalOpened" :is-create-modal-opened="isCreateModalOpened" :refresh-data="getSensors"
    @toggle-modal="isCreateModalOpened = false" />
  <assign-form v-if="isAssignModalOpened" :is-assign-modal-opened="isAssignModalOpened" :refresh-data="getSensors"
    :sensor="selectedSensor" @toggle-modal="toggleAssignModal" @override-complete="getSensors" />
  <spot-assign-history-modal v-if="isSpotAssignHistoryModalOpened"
    :is-spot-assign-history-modal-opened="isSpotAssignHistoryModalOpened" :refresh-data="getSensors"
    :sensor="selectedSensor" @toggle-modal="isSpotAssignHistoryModalOpened = false" />

  <sensor-sent-packets v-if="isSensorPacketsSentDisplay" :is-sensor-packets-sent-displayed="isSensorPacketsSentDisplay"
    :mac="selectedSensor.mac" :title="`${selectedSensor.space ?? ''} ${selectedSensor.spot ?? ''} ${selectedSensor.space || selectedSensor.spot ? '-' : ''
      } ${selectedSensor.mac}`" @toggle-modal="isSensorPacketsSentDisplay = false" :fromProp="from" :toProp="to" />
  <main-section>
    <loader v-if="isLoading" />
    <div v-else class="h-full">
      <table-action-header :icon="['fas', 'add']" :permission-id="permissions.CreateAssignment" button-text="Create"
        :title="$t('Sensors.Title')" @execute-method="toggleCreateModal" />
      <custom-table :allow-auto-resize="true" :assign-column="true" :spot-assign-history="true"
        :is-action-list-not-displayed="true" :is-data-source-loading="isDataSourceLoading" :render-summary="['mac']"
        :show-per-page="16" :table-data="sensors" :table-headers="tableHeaders" table-height="93%"
        @force-refresh="getSensors" @view-sensor-packets="toggleSensorSentPackets" @assign-sensor="toggleAssignModal"
        @spot-assign-history="toggleSpotAssignHistoryModal" @on-cell-prepared="onCellPrepared" />
      <dx-tooltip ref="tooltipRef">
        <div class="text-left text-xs">
          <div>
            <b>{{ $t("Sensors.Tooltip.PacketsFromFirmware") }}</b> {{ tooltipSensor.packetsSendCount }}
          </div>
          <div>
            <b>{{ $t("Sensors.Tooltip.SavedPackets") }}</b> {{ tooltipSensor.packetSendSum }}
          </div>
        </div>
      </dx-tooltip>
    </div>
  </main-section>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import MainSection from "@/core/Components/MainSection.vue";
import TableActionHeader from "@/core/Components/TableActionHeader.vue";
import CustomTable from "@/core/Components/CustomTable.vue";
import CreateForm from "../Components/CreateForm.vue";
import SensorService from "@/modules/Parkom/Submodules/Sensors/Services/SensorService";
import { Sensor } from "@/modules/Parkom/Submodules/Sensors/Services/SensorTypes";
import { useToast } from "vue-toastification";
import { ErrorMessageFormatter } from "@/core/Services/GlobalFunctions";
import Loader from "@/core/Components/Loader.vue";
import SensorSentPackets from "@/modules/Parkom/Submodules/Sensors/Components/SensorSentPackets.vue";
import AssignForm from "@/modules/Parkom/Submodules/ParkingSpots/Components/AssignForm.vue";
import SpotAssignHistoryModal from "@/modules/Parkom/Submodules/ParkingSpots/Components/SpotAssignHistoryModal.vue";
import { useChangeCompanyStore } from "@/core/Store/changeCompanyStore";
import { Permissions } from "@/core/Services/Enums";
import moment from "moment";
import { useParkingSpaceFilterStore } from "@/core/Store/useParkingSpaceFilterStore";
import { on } from "devextreme/events";
import DxTooltip from "devextreme-vue/tooltip";
import DeviceDataService, { DeviceLatestData } from "@/modules/Devices/Services/DeviceDataService";

export default defineComponent({
  components: {
    SensorSentPackets,
    Loader,
    CreateForm,
    TableActionHeader,
    MainSection,
    CustomTable,
    AssignForm,
    SpotAssignHistoryModal,
    DxTooltip,
  },
  data() {
    return {
      from: moment().startOf("day").utc().format("yyyy-MM-DD HH:mm[Z]"),
      to: moment().endOf("day").utc().format("yyyy-MM-DD HH:mm[Z]"),
      sensorService: new SensorService(),
      deviceDataService: new DeviceDataService(),
      toast: useToast(),
      changeCompanyStore: useChangeCompanyStore(),
      isDataSourceLoading: false,
      isLoading: false,
      isCreateModalOpened: false,
      isSensorPacketsSentDisplay: false,
      isAssignModalOpened: false,
      isSpotAssignHistoryModalOpened: false,
      selectedSensor: {} as Sensor,
      sensors: [] as Sensor[],
      tooltipSensor: {} as Sensor,
      permissions: Permissions,
      lastSensorPackets: [] as DeviceLatestData[],
      tableHeaders: {
        id: { header: "ID", visible: false },
        deviceUuid: { header: "UUID", visible: false },
        mac: { header: "Sensors.MAC", visible: true },
        space: { header: "Sensors.Space", visible: true },
        spaceId: { header: "Sensors.SpaceId", visible: false },
        spot: { header: "Sensors.Spot", visible: true },
        imei: { header: "Sensors.IMEI", visible: true },
        spotOccupied: { header: "Sensors.Occupied", visible: true },
        lastSync: { header: "Sensors.LastSync", visible: true },
        firmwareVersion: { header: "Sensors.FirmwareVersion", visible: true },
        mag1Temp: { header: "Sensors.Temperature", visible: true },
        packetsSentMerged: { header: "Sensors.Packets", visible: true },
        batteryLevel: { header: "Sensors.BatteryLevel", visible: true },
        voltage: { header: "Sensors.Voltage", visible: true },
        unhandledRestartCounter: {
          header: "Sensors.UnhandledReboots",
          visible: true,
        },
        rssi: { header: "Sensors.RSSI", visible: true },
        rsrq: { header: "Sensors.RSRQ", visible: true },
        searchingForNetworkCount: {
          header: "Sensors.SearchingForNetwork",
          visible: true,
        },
        cellId: { header: "Sensors.Cell", visible: true },
        band: { header: "Sensors.Band", visible: true },
        ecl: { header: "Sensors.ECL", visible: true },
        rsrp: { header: "Sensors.RSRP", visible: true },
        snr: { header: "Sensors.SNR", visible: true },
        totalConsumptionMAh: {
          header: "Total consumption (mAH)",
          visible: true,
        },
        imsi: { header: "Sensors.IMSI", visible: true },
        ccid: { header: "Sensors.CCID", visible: true },
        hwRevision: { header: "HwRevision", visible: true },
        powerConsSensor: { header: "PowerConsSensor", visible: true },
        rtcVersion: { header: "RtcVersion", visible: true },
        magnetometerVersion: { header: "MagnetometerVersion", visible: true },
        modemDisableCounter: {
          header: "Sensors.ModemDisableCounter",
          visible: true,
        },
        noResponseCounter: {
          header: "Sensors.NoResponseCounter",
          visible: true,
        },
      },
      parkingSpaceFilterStore: useParkingSpaceFilterStore(),
    };
  },
  async created() {
    this.isLoading = true;
    try {
      await this.getSensors();
    } catch (error) {
      this.toast.error(ErrorMessageFormatter(error));
    } finally {
      this.isLoading = false;
    }
  },

  watch: {
    async "changeCompanyStore.companyId"() {
      await this.getSensors();
    },
    async "parkingSpaceFilterStore.selectedParkingSpaceIds"(value: number[]) {
      this.getSensors();
    },
  },
  methods: {
    async getSensors() {
      this.isDataSourceLoading = true;
      try {
        this.sensors = await this.sensorService.getSensorsFilter({
          pageNumber: 1,
          pageSize: 100000,
          parkingSpaceIds: this.parkingSpaceFilterStore.selectedParkingSpaceIds,
        });

        const uuids = this.sensors.map(
          (responseMessage: Sensor) => responseMessage.deviceUuid
        );
        await this.getLastSensorFirmwareUpdateData(uuids);

      } catch (error) {
        this.toast.error(ErrorMessageFormatter(error));
      } finally {
        this.isDataSourceLoading = false;
      }
    },

    async getLastSensorFirmwareUpdateData(uuids: string[]) {
      if (uuids.length > 0) {
        try {
          this.lastSensorPackets = await this.deviceDataService.getDevicesLatestData({
            deviceUuids: uuids
          });
        } catch (error) {
          console.log(error);
        }       

        if (this.lastSensorPackets.length > 0) {
          this.sensors.forEach(sensor => {
            sensor.firmwareUpdateStatus = this.findFirmwareUpdateStatus(sensor.deviceUuid)
          });
        }
      }
    },

    toggleCreateModal() {
      this.isCreateModalOpened = !this.isCreateModalOpened;
    },

    toggleSpotAssignHistoryModal(sensor: Sensor) {
      this.selectedSensor = sensor;
      this.isSpotAssignHistoryModalOpened = !this.isSpotAssignHistoryModalOpened;
    },

    toggleSensorSentPackets(sensor: Sensor) {
      this.selectedSensor = sensor;
      this.isSensorPacketsSentDisplay = !this.isSensorPacketsSentDisplay;
    },

    toggleAssignModal(sensor: Sensor) {
      this.selectedSensor = sensor;
      this.isAssignModalOpened = !this.isAssignModalOpened;
    },
    onCellPrepared(e: any) {
      if (e.column.dataField === "packetsSentMerged") {
        const tooltip: any = this.$refs?.tooltipRef;

        on(e.cellElement?.children[0], "mouseover", (arg: any) => {
          this.tooltipSensor = e.data;
          tooltip.instance.show(arg.target);
        });

        on(e.cellElement?.children[0], "mouseout", () => {
          tooltip.instance.hide();
        });
      }
    },

    findFirmwareUpdateStatus(deviceUuid: string) {
      const data = this.lastSensorPackets.find(
        (sentData) => sentData.deviceUuid === deviceUuid
      )?.data;

      if (data) {
        const newestPacket = data.reduce((latest, current) => {
          return new Date(current.dateTime) > new Date(latest.dateTime) ? current : latest;
        });
        
        return newestPacket.channel === 4;
      } else {
        return false;
      }
    }
  },
});
</script>

<style scoped></style>
